// src/config/languages.ts
//
// Doctor Is In App — shared language registry
//
// This is the frontend source of truth for supported languages.
// Student, Parent/Teacher, Advisor, and ERH should import from here
// instead of maintaining separate hard-coded language lists.

export type SupportedLanguage = {
  code: string;
  label: string;
  nativeLabel: string;
  rtl?: boolean;
};

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  { code: "en", label: "English", nativeLabel: "English" },

  { code: "af", label: "Afrikaans", nativeLabel: "Afrikaans" },
  { code: "sq", label: "Albanian", nativeLabel: "Shqip" },
  { code: "am", label: "Amharic", nativeLabel: "አማርኛ" },
  { code: "ar", label: "Arabic", nativeLabel: "العربية", rtl: true },
  { code: "hy", label: "Armenian", nativeLabel: "Հայերեն" },
  { code: "as", label: "Assamese", nativeLabel: "অসমীয়া" },
  { code: "ay", label: "Aymara", nativeLabel: "Aymar aru" },
  { code: "az", label: "Azerbaijani", nativeLabel: "Azərbaycan dili" },

  { code: "bm", label: "Bambara", nativeLabel: "Bamanankan" },
  { code: "eu", label: "Basque", nativeLabel: "Euskara" },
  { code: "be", label: "Belarusian", nativeLabel: "Беларуская" },
  { code: "bn", label: "Bengali", nativeLabel: "বাংলা" },
  { code: "bho", label: "Bhojpuri", nativeLabel: "भोजपुरी" },
  { code: "bs", label: "Bosnian", nativeLabel: "Bosanski" },
  { code: "bg", label: "Bulgarian", nativeLabel: "Български" },

  { code: "ca", label: "Catalan", nativeLabel: "Català" },
  { code: "ceb", label: "Cebuano", nativeLabel: "Cebuano" },
  {
    code: "zh",
    label: "Chinese (Simplified)",
    nativeLabel: "简体中文",
  },
  {
    code: "zh-TW",
    label: "Chinese (Traditional)",
    nativeLabel: "繁體中文",
  },
  { code: "co", label: "Corsican", nativeLabel: "Corsu" },
  { code: "hr", label: "Croatian", nativeLabel: "Hrvatski" },
  { code: "cs", label: "Czech", nativeLabel: "Čeština" },

  { code: "da", label: "Danish", nativeLabel: "Dansk" },
  { code: "dv", label: "Dhivehi", nativeLabel: "ދިވެހި", rtl: true },
  { code: "doi", label: "Dogri", nativeLabel: "डोगरी" },
  { code: "nl", label: "Dutch", nativeLabel: "Nederlands" },

  { code: "eo", label: "Esperanto", nativeLabel: "Esperanto" },
  { code: "et", label: "Estonian", nativeLabel: "Eesti" },
  { code: "ee", label: "Ewe", nativeLabel: "Eʋegbe" },

  { code: "fil", label: "Filipino", nativeLabel: "Filipino" },
  { code: "fi", label: "Finnish", nativeLabel: "Suomi" },
  { code: "fr", label: "French", nativeLabel: "Français" },
  { code: "fy", label: "Frisian", nativeLabel: "Frysk" },

  { code: "gl", label: "Galician", nativeLabel: "Galego" },
  { code: "ka", label: "Georgian", nativeLabel: "ქართული" },
  { code: "de", label: "German", nativeLabel: "Deutsch" },
  { code: "el", label: "Greek", nativeLabel: "Ελληνικά" },
  { code: "gn", label: "Guarani", nativeLabel: "Avañe'ẽ" },
  { code: "gu", label: "Gujarati", nativeLabel: "ગુજરાતી" },

  {
    code: "ht",
    label: "Haitian Creole",
    nativeLabel: "Kreyòl ayisyen",
  },
  { code: "ha", label: "Hausa", nativeLabel: "Hausa" },
  { code: "haw", label: "Hawaiian", nativeLabel: "ʻŌlelo Hawaiʻi" },
  { code: "he", label: "Hebrew", nativeLabel: "עברית", rtl: true },
  { code: "hi", label: "Hindi", nativeLabel: "हिन्दी" },
  { code: "hmn", label: "Hmong", nativeLabel: "Hmoob" },
  { code: "hu", label: "Hungarian", nativeLabel: "Magyar" },

  { code: "is", label: "Icelandic", nativeLabel: "Íslenska" },
  { code: "ig", label: "Igbo", nativeLabel: "Igbo" },
  { code: "ilo", label: "Ilocano", nativeLabel: "Ilocano" },
  { code: "id", label: "Indonesian", nativeLabel: "Bahasa Indonesia" },
  { code: "ga", label: "Irish", nativeLabel: "Gaeilge" },
  { code: "it", label: "Italian", nativeLabel: "Italiano" },

  { code: "ja", label: "Japanese", nativeLabel: "日本語" },
  { code: "jv", label: "Javanese", nativeLabel: "Basa Jawa" },

  { code: "kn", label: "Kannada", nativeLabel: "ಕನ್ನಡ" },
  { code: "kk", label: "Kazakh", nativeLabel: "Қазақ тілі" },
  { code: "km", label: "Khmer", nativeLabel: "ខ្មែរ" },
  { code: "rw", label: "Kinyarwanda", nativeLabel: "Ikinyarwanda" },
  { code: "gom", label: "Konkani", nativeLabel: "कोंकणी" },
  { code: "ko", label: "Korean", nativeLabel: "한국어" },
  { code: "kri", label: "Krio", nativeLabel: "Krio" },
  { code: "ku", label: "Kurdish", nativeLabel: "Kurdî" },
  {
    code: "ckb",
    label: "Kurdish (Sorani)",
    nativeLabel: "کوردی",
    rtl: true,
  },
  { code: "ky", label: "Kyrgyz", nativeLabel: "Кыргызча" },

  { code: "lo", label: "Lao", nativeLabel: "ລາວ" },
  { code: "la", label: "Latin", nativeLabel: "Latina" },
  { code: "lv", label: "Latvian", nativeLabel: "Latviešu" },
  { code: "ln", label: "Lingala", nativeLabel: "Lingála" },
  { code: "lt", label: "Lithuanian", nativeLabel: "Lietuvių" },
  { code: "lg", label: "Luganda", nativeLabel: "Luganda" },
  { code: "lb", label: "Luxembourgish", nativeLabel: "Lëtzebuergesch" },

  { code: "mk", label: "Macedonian", nativeLabel: "Македонски" },
  { code: "mai", label: "Maithili", nativeLabel: "मैथिली" },
  { code: "mg", label: "Malagasy", nativeLabel: "Malagasy" },
  { code: "ms", label: "Malay", nativeLabel: "Bahasa Melayu" },
  { code: "ml", label: "Malayalam", nativeLabel: "മലയാളം" },
  { code: "mt", label: "Maltese", nativeLabel: "Malti" },
  { code: "mi", label: "Māori", nativeLabel: "Te Reo Māori" },
  { code: "mr", label: "Marathi", nativeLabel: "मराठी" },
  { code: "mni-Mtei", label: "Meitei", nativeLabel: "ꯃꯤꯇꯩꯂꯣꯟ" },
  { code: "lus", label: "Mizo", nativeLabel: "Mizo ṭawng" },
  { code: "mn", label: "Mongolian", nativeLabel: "Монгол" },
  { code: "my", label: "Myanmar (Burmese)", nativeLabel: "မြန်မာ" },

  { code: "ne", label: "Nepali", nativeLabel: "नेपाली" },
  { code: "no", label: "Norwegian", nativeLabel: "Norsk" },
  { code: "ny", label: "Nyanja", nativeLabel: "Chichewa" },

  { code: "or", label: "Odia", nativeLabel: "ଓଡ଼ିଆ" },
  { code: "om", label: "Oromo", nativeLabel: "Afaan Oromoo" },

  { code: "ps", label: "Pashto", nativeLabel: "پښتو", rtl: true },
  { code: "fa", label: "Persian", nativeLabel: "فارسی", rtl: true },
  { code: "pl", label: "Polish", nativeLabel: "Polski" },
  { code: "pt", label: "Portuguese", nativeLabel: "Português" },
  { code: "pa", label: "Punjabi", nativeLabel: "ਪੰਜਾਬੀ" },

  { code: "qu", label: "Quechua", nativeLabel: "Runasimi" },

  { code: "ro", label: "Romanian", nativeLabel: "Română" },
  { code: "ru", label: "Russian", nativeLabel: "Русский" },

  { code: "sm", label: "Samoan", nativeLabel: "Gagana Samoa" },
  { code: "sa", label: "Sanskrit", nativeLabel: "संस्कृतम्" },
  { code: "gd", label: "Scots Gaelic", nativeLabel: "Gàidhlig" },
  { code: "nso", label: "Sepedi", nativeLabel: "Sepedi" },
  { code: "sr", label: "Serbian", nativeLabel: "Српски" },
  { code: "st", label: "Sesotho", nativeLabel: "Sesotho" },
  { code: "sn", label: "Shona", nativeLabel: "ChiShona" },
  { code: "sd", label: "Sindhi", nativeLabel: "سنڌي", rtl: true },
  { code: "si", label: "Sinhala", nativeLabel: "සිංහල" },
  { code: "sk", label: "Slovak", nativeLabel: "Slovenčina" },
  { code: "sl", label: "Slovenian", nativeLabel: "Slovenščina" },
  { code: "so", label: "Somali", nativeLabel: "Soomaali" },
  { code: "es", label: "Spanish", nativeLabel: "Español" },
  { code: "su", label: "Sundanese", nativeLabel: "Basa Sunda" },
  { code: "sw", label: "Swahili", nativeLabel: "Kiswahili" },
  { code: "sv", label: "Swedish", nativeLabel: "Svenska" },

  { code: "tl", label: "Tagalog", nativeLabel: "Tagalog" },
  { code: "tg", label: "Tajik", nativeLabel: "Тоҷикӣ" },
  { code: "ta", label: "Tamil", nativeLabel: "தமிழ்" },
  { code: "tt", label: "Tatar", nativeLabel: "Татарча" },
  { code: "te", label: "Telugu", nativeLabel: "తెలుగు" },
  { code: "th", label: "Thai", nativeLabel: "ไทย" },
  { code: "ti", label: "Tigrinya", nativeLabel: "ትግርኛ" },
  { code: "ts", label: "Tsonga", nativeLabel: "Xitsonga" },
  { code: "tr", label: "Turkish", nativeLabel: "Türkçe" },
  { code: "tk", label: "Turkmen", nativeLabel: "Türkmençe" },
  { code: "ak", label: "Twi", nativeLabel: "Twi" },

  { code: "uk", label: "Ukrainian", nativeLabel: "Українська" },
  { code: "ur", label: "Urdu", nativeLabel: "اردو", rtl: true },
  { code: "ug", label: "Uyghur", nativeLabel: "ئۇيغۇرچە", rtl: true },
  { code: "uz", label: "Uzbek", nativeLabel: "Oʻzbekcha" },

  { code: "vi", label: "Vietnamese", nativeLabel: "Tiếng Việt" },

  { code: "cy", label: "Welsh", nativeLabel: "Cymraeg" },

  { code: "xh", label: "Xhosa", nativeLabel: "isiXhosa" },

  { code: "yi", label: "Yiddish", nativeLabel: "ייִדיש", rtl: true },
  { code: "yo", label: "Yoruba", nativeLabel: "Yorùbá" },

  { code: "zu", label: "Zulu", nativeLabel: "isiZulu" },
];

export const DEFAULT_LANGUAGE_CODE = "en";

export const LANGUAGE_BY_CODE = new Map(
  SUPPORTED_LANGUAGES.map((language) => [language.code.toLowerCase(), language]),
);

export function getLanguageByCode(
  code?: string | null,
): SupportedLanguage | undefined {
  const value = String(code || "").trim().toLowerCase();
  if (!value) return undefined;
  return LANGUAGE_BY_CODE.get(value);
}

export function getLanguageByLabel(
  label?: string | null,
): SupportedLanguage | undefined {
  const value = String(label || "").trim().toLowerCase();
  if (!value) return undefined;

  return SUPPORTED_LANGUAGES.find(
    (language) =>
      language.label.toLowerCase() === value ||
      language.nativeLabel.toLowerCase() === value,
  );
}

export function getLanguageCode(value?: string | null): string {
  const raw = String(value || "").trim();
  if (!raw) return DEFAULT_LANGUAGE_CODE;

  return (
    getLanguageByCode(raw)?.code ||
    getLanguageByLabel(raw)?.code ||
    raw
  );
}

export function getLanguageLabel(value?: string | null): string {
  const raw = String(value || "").trim();
  if (!raw) return "English";

  return (
    getLanguageByCode(raw)?.label ||
    getLanguageByLabel(raw)?.label ||
    raw
  );
}

export function isRtlLanguage(value?: string | null): boolean {
  const raw = String(value || "").trim();

  return !!(
    getLanguageByCode(raw)?.rtl ||
    getLanguageByLabel(raw)?.rtl
  );
}

/**
 * Useful for existing components that expect:
 *
 * [
 *   { value: "English", label: "English" },
 *   ...
 * ]
 */
export const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map((language) => ({
  value: language.label,
  label:
    language.nativeLabel === language.label
      ? language.label
      : `${language.label} — ${language.nativeLabel}`,
  code: language.code,
  rtl: !!language.rtl,
}));

/**
 * Compatibility map for older portal code that still works with
 * English language names rather than ISO-ish language codes.
 */
export const LANGUAGE_CODES: Record<string, string> =
  Object.fromEntries(
    SUPPORTED_LANGUAGES.map((language) => [
      language.label,
      language.code,
    ]),
  );